import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import { API_BASE, authFetch, getAccessToken } from "../utils/auth";

interface Release {
  id: number;
  version: string;
  file_size: number;
  release_notes: string;
  is_current: boolean;
  is_beta: boolean;
  min_tier: string;
  created_at: string;
  download_count: number;
}

interface ReleasesResponse {
  current?: Release;
  beta?: Release;
}

interface UserTier {
  user_id: string;
  email: string;
  claims_tier: string;
  can_download: boolean;
}

interface DownloadLink {
  download_url: string;
  expires_at: string;
  version: string;
  file_size: number;
  checksum_sha256: string;
}

const DownloadPage: React.FC = () => {
  const [releases, setReleases] = useState<ReleasesResponse | null>(null);
  const [userTier, setUserTier] = useState<UserTier | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!getAccessToken()) {
        navigate("/login");
        return;
      }

      try {
        const releasesRes = await fetch(`${API_BASE}/api/software/releases`);
        if (releasesRes.ok) {
          const releasesData = await releasesRes.json();
          setReleases(releasesData);
        }

        const tierRes = await authFetch(`${API_BASE}/api/user/tier`);

        if (tierRes.ok) {
          const tierData = await tierRes.json();
          setUserTier(tierData);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load download information");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleDownload = async (version: string) => {
    if (!getAccessToken()) {
      navigate("/login");
      return;
    }

    setDownloading(version);
    setError("");

    try {
      const res = await authFetch(
        `${API_BASE}/api/software/download-link/${version}`,
      );

      if (res.status === 403) {
        setError("Your account tier does not have access to this download. Please upgrade to continue.");
        setDownloading(null);
        return;
      }

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || "Failed to get download link");
      }

      const data: DownloadLink = await res.json();
      window.location.href = data.download_url;
    } catch (err: any) {
      console.error("Download error:", err);
      setError(err.message || "Failed to start download");
    } finally {
      setDownloading(null);
    }
  };

  const canDownloadRelease = (release: Release): boolean => {
    if (!userTier) return false;

    const tierLevel: { [key: string]: number } = {
      "none": 0,
      "free": 1,
      "premium": 2,
    };

    const userLevel = tierLevel[userTier.claims_tier] || 0;
    const minLevel = tierLevel[release.min_tier] || 1;

    return userLevel >= minLevel;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0b0f]">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-zinc-500">Loading...</div>
        </div>
      </div>
    );
  }

  const renderCurrentRelease = () => {
    if (!releases?.current) return null;
    const rel = releases.current;
    const canDownload = canDownloadRelease(rel);
    const isDownloading = downloading === rel.version;

    return (
      <div className="card-dark rounded-xl p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-medium text-zinc-100">
              Version {rel.version}
              <span className="ml-2 badge badge-success">Current</span>
            </h2>
            <p className="text-zinc-500 text-sm mt-1">
              Released {formatDate(rel.created_at)}
            </p>
          </div>
          <span className="text-zinc-500 text-sm">
            {formatFileSize(rel.file_size)}
          </span>
        </div>

        {rel.release_notes && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-zinc-400 mb-2">Release Notes</h3>
            <p className="text-zinc-300 text-sm whitespace-pre-wrap">
              {rel.release_notes}
            </p>
          </div>
        )}

        <button
          onClick={() => handleDownload(rel.version)}
          disabled={!canDownload || isDownloading}
          className={`w-full py-3 rounded-xl font-medium transition ${
            canDownload
              ? "btn-primary"
              : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
          }`}
        >
          {isDownloading
            ? "Preparing Download..."
            : canDownload
            ? "Download for Windows"
            : "Requires " + rel.min_tier + " tier"}
        </button>

        <p className="text-center text-zinc-600 text-xs mt-3">
          {rel.download_count.toLocaleString()} downloads
        </p>
      </div>
    );
  };

  const renderBetaRelease = () => {
    if (!releases?.beta) return null;
    const rel = releases.beta;
    const canDownload = canDownloadRelease(rel);
    const isDownloading = downloading === rel.version;

    return (
      <div className="card-dark rounded-xl p-6 border-orange-500/30">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-medium text-zinc-100">
              Version {rel.version}
              <span className="ml-2 badge badge-warning">Beta</span>
            </h2>
            <p className="text-zinc-500 text-sm mt-1">
              Released {formatDate(rel.created_at)}
            </p>
          </div>
          <span className="text-zinc-500 text-sm">
            {formatFileSize(rel.file_size)}
          </span>
        </div>

        {rel.release_notes && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-zinc-400 mb-2">What's New</h3>
            <p className="text-zinc-300 text-sm whitespace-pre-wrap">
              {rel.release_notes}
            </p>
          </div>
        )}

        <button
          onClick={() => handleDownload(rel.version)}
          disabled={!canDownload || isDownloading}
          className={`w-full py-3 rounded-xl font-medium transition ${
            canDownload
              ? "bg-orange-500 hover:bg-orange-600 text-white"
              : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
          }`}
        >
          {isDownloading
            ? "Preparing Download..."
            : canDownload
            ? "Download Beta"
            : "Requires " + rel.min_tier + " tier"}
        </button>

        <p className="text-center text-zinc-600 text-xs mt-3">
          Beta versions may contain bugs. Use at your own risk.
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0b0f]">
      <Navigation />
      <div className="py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 mb-4 shadow-lg shadow-emerald-500/20">
              <svg className="w-7 h-7 text-[#0a0b0f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold text-zinc-100">
              Downloads
            </h1>
            <p className="text-zinc-500 mt-2 text-sm">
              Get the apps for your workflow
            </p>

            {userTier && (
              <p className="text-zinc-500 mt-1 text-sm">
                Account: {userTier.email} <span className="text-amber-400">({userTier.claims_tier} tier)</span>
              </p>
            )}
          </div>

          {error && (
            <div className="alert-error mb-6">{error}</div>
          )}

          {/* Recorder Application Section */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-sky-600 shadow-lg shadow-sky-500/20">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-zinc-100">Recorder Application</h2>
                <p className="text-zinc-500 text-sm">iPhone &middot; Field audio + photo capture with AI transcription</p>
              </div>
            </div>

            <div className="card-dark rounded-xl p-6">
              <p className="text-zinc-300 text-sm mb-4">
                Capture inspection audio, photos, and notes from the field. Transcribes recordings automatically and syncs to your Google Drive and claim record. Now available on the Apple App Store.
              </p>

              <a
                href="https://apps.apple.com/app/recorder-application/id6765809745"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-medium transition bg-sky-500 hover:bg-sky-600 text-white"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                Download on the App Store
              </a>

              <p className="text-center text-zinc-600 text-xs mt-3">
                Requires iOS 16 or later
              </p>

              <p className="text-center text-zinc-600 text-xs mt-2">
                Beta tester?{" "}
                <a
                  href="https://testflight.apple.com/join/GA1b9zZV"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-400 hover:text-sky-300 hover:underline"
                >
                  Join via TestFlight
                </a>
              </p>
            </div>
          </div>

          {/* Claims Management App Section */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg shadow-emerald-500/20">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-zinc-100">Claims Management App</h2>
                <p className="text-zinc-500 text-sm">Windows &middot; Claims administration software</p>
              </div>
            </div>

            {userTier && !userTier.can_download && (
              <div className="alert-warning mb-4">
                <strong>Account Upgrade Required</strong>
                <p className="text-sm mt-1 opacity-80">
                  Your current account tier does not include software downloads.
                  Please upgrade to the free or premium tier to download the Claims Management application.
                </p>
              </div>
            )}

            {renderCurrentRelease()}
            {renderBetaRelease()}

            {!releases?.current && !releases?.beta && (
              <div className="text-center text-zinc-500 py-8 card-dark rounded-xl">
                No releases available yet. Check back soon!
              </div>
            )}
          </div>

          <div className="mt-6 text-center">
            <a
              href="/uploads"
              className="text-amber-400 hover:text-amber-300 text-sm hover:underline"
            >
              Back to Dashboard
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
